import React, { useState, useEffect } from 'react';
import { globeService } from '../../services/globeService';
import './VesselComplianceBadge.css';

/**
 * Sanctions / Risk & Compliance status pill for a single hull (keyed by IMO).
 *
 * Self-contained: it fetches its own compliance status from the backend, which
 * is config-gated. When vessel screening is not enabled (no provider key) the
 * backend returns {configured: false} and we render a calm neutral chip — never
 * an error. When enabled, the pill turns green (Clear) / amber (Unknown) /
 * red (Flagged), with matched sanction lists on hover.
 *
 * No vendor/provider name ever appears in any label or tooltip.
 */
const STATUS_META = {
  clean: { label: 'Clear', cls: 'vcb-clean' },
  flagged: { label: 'Flagged', cls: 'vcb-flagged' },
  unknown: { label: 'Unknown', cls: 'vcb-unknown' },
  warning: { label: 'Warning', cls: 'vcb-warning' },
};

function VesselComplianceBadge({ imo }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let active = true;
    if (!imo) {
      setState({ loading: false, configured: false });
      return undefined;
    }
    setState({ loading: true });
    globeService
      .getVesselCompliance(imo)
      .then((res) => {
        if (active) setState({ loading: false, ...(res || {}) });
      })
      .catch(() => {
        // Compliance is advisory — a lookup failure must never break the row.
        if (active) setState({ loading: false, error: true });
      });
    return () => {
      active = false;
    };
  }, [imo]);

  if (state.loading) {
    return (
      <span className="vessel-compliance-badge vcb-loading" title="Checking vessel sanctions status…">
        …
      </span>
    );
  }

  // Never surface a failed lookup as scary UI.
  if (state.error) {
    return null;
  }

  if (!state.configured) {
    return (
      <span className="vessel-compliance-badge vcb-off" title="Vessel sanctions screening is not enabled">
        Not screened
      </span>
    );
  }

  // "warning" = sanctions-clean but the screening raised operational or
  // management risk indicators (AIS gaps, PSC detentions, cover lapses…).
  const statusKey =
    state.sanctions_status === 'clean' && state.warning ? 'warning' : state.sanctions_status;
  const meta = STATUS_META[statusKey] || STATUS_META.unknown;
  const lists = (state.sanction_lists_matched || []).join(', ');
  let title = `Vessel sanctions screening: ${meta.label}`;
  if (statusKey === 'flagged' && lists) {
    title = `Sanctions match: ${lists}`;
  } else if (statusKey === 'warning') {
    title = 'No sanctions match, but risk indicators were detected for this vessel';
  }

  return (
    <span className={`vessel-compliance-badge ${meta.cls}`} title={title}>
      {meta.label}
    </span>
  );
}

export default VesselComplianceBadge;
